import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Label } from '@ordo/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { Separator } from '@ordo/ui/separator'
import { Skeleton } from '@ordo/ui/skeleton'
import { BackLink } from 'compositions/BackLink'
import { Page } from 'compositions/Page'
import { useSenders } from 'hooks/api'
import { useCreateInboundForwarding, useInboundForwardings } from 'hooks/api/useInbound'
import { Save } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { inboundSenderOptions, isInboundDestinationValid } from '../../lib/inbound'
import { ForwardingDestinationFields } from './inbound-forwarding-fields'
import { InboundError, RecognizedOnlyField } from './inbound-shared'

export function AdminInboundNew() {
  const navigate = useNavigate()
  const forwardings = useInboundForwardings()
  const senders = useSenders('SMS')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [destinationUrl, setDestinationUrl] = useState('')
  const [audience, setAudience] = useState('')
  const [recognizedOnly, setRecognizedOnly] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const create = useCreateInboundForwarding()
  const initialLoading = (forwardings.loading && !forwardings.data) || (senders.loading && !senders.data)
  const readError = forwardings.error || senders.error
  const availableSenders = inboundSenderOptions(senders.data ?? [], forwardings.data ?? [])
  const ready =
    !readError &&
    availableSenders.some((sender) => sender.phoneNumber === phoneNumber) &&
    isInboundDestinationValid(destinationUrl.trim(), audience.trim())
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!ready || create.loading) {
      return
    }
    setError(null)
    try {
      const created = await create.perform({
        phoneNumber,
        destinationUrl: destinationUrl.trim(),
        audience: audience.trim(),
        recognizedOnly,
      })
      navigate(`/inbound/${created.id}`, { replace: true })
    } catch (failure) {
      setError(failure)
    }
  }

  return (
    <Page>
      <BackLink to="/inbound">Back to Inbound</BackLink>
      <Card>
        <CardHeader>
          <CardTitle>New Handler</CardTitle>
        </CardHeader>
        <Separator />
        <CardContent className="pt-6">
          {readError ? <InboundError error={readError} /> : null}
          {initialLoading ? (
            <div className="space-y-5" role="status" aria-label="Loading handler form">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-10 w-full max-w-lg" />
              <div className="flex justify-end">
                <Skeleton className="h-9 w-36" />
              </div>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={(event) => void submit(event)}>
              {availableSenders.length === 0 && !readError ? (
                <p className="text-sm text-muted-foreground">
                  No numbers are available. Add an active, verified SMS number under{' '}
                  <Link className="underline" to="/admin/senders">
                    Senders
                  </Link>
                  , or open an existing handler from{' '}
                  <Link className="underline" to="/inbound">
                    Inbound
                  </Link>{' '}
                  to edit its configuration.
                </p>
              ) : null}
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="inbound-number">Receiving number</Label>
                <Select
                  value={phoneNumber}
                  onValueChange={setPhoneNumber}
                  disabled={create.loading || !availableSenders.length || Boolean(readError)}>
                  <SelectTrigger id="inbound-number" className="w-full min-w-0">
                    <SelectValue placeholder="Choose an SMS number" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSenders.map((sender) => (
                      <SelectItem key={sender.id} value={sender.phoneNumber}>
                        {sender.nickname} ({sender.phoneNumber})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <ForwardingDestinationFields
                idPrefix="inbound-destination"
                destinationUrl={destinationUrl}
                audience={audience}
                onDestinationUrlChange={setDestinationUrl}
                onAudienceChange={setAudience}
                disabled={create.loading}
              />
              <RecognizedOnlyField
                id="inbound-recognized"
                checked={recognizedOnly}
                onChange={setRecognizedOnly}
                disabled={create.loading}
              />
              {error ? <InboundError error={error} /> : null}
              <div className="flex justify-end">
                <Button type="submit" disabled={create.loading || !ready}>
                  <Save className="h-4 w-4" /> {create.loading ? 'Creating...' : 'Create Handler'}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </Page>
  )
}
